import {
  ChangeDetectionStrategy,
  Component,
  signal,
  OnInit,
  input,
  output,
  OnDestroy,
  inject,
} from "@angular/core";
import { FormArray, FormBuilder, FormGroup, UntypedFormGroup } from "@angular/forms";

import { Subscription } from "rxjs";
import { IConditionForm, IConditionGroupForm, IConditionItemForm } from "src/app/shared/interfaces";

import { RulesetsBuilderService } from "@components/settings/settings-rulesets/rulesets-builder.service";
import { InputSelectOption } from "@components/shared/inputs/input-select/input-select.model";
import { RulesetBooleanOperatorEnum } from "@shared/enums";
import { CustomValidators } from "@shared/validators";

enum MatchType {
  ALL,
  ANY,
}

@Component({
  standalone: false,
  selector: "app-ruleset-criteria",
  templateUrl: "./ruleset-criteria.component.html",
  styleUrl: "./ruleset-criteria.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RulesetCriteriaComponent implements OnInit, OnDestroy {
  formGroup = input.required<FormGroup<IConditionForm>>();

  propertyOptions = input<InputSelectOption[]>();

  locationTypeOptions = input<InputSelectOption[]>([]);

  locationOptions = input<InputSelectOption[]>([]);

  isReadOnly = input<boolean>(false);

  countryOptions = input<InputSelectOption[]>([]);

  addCriteria = output<UntypedFormGroup>();

  removeCriteria = output<{ formGroup: UntypedFormGroup; index: number }>();

  selectedMatchTypeIndex = signal(MatchType.ALL);

  private readonly rulesetsBuilderService = inject(RulesetsBuilderService);

  private readonly formBuilder = inject(FormBuilder);

  private subscriptions = new Subscription();

  readonly matchType = {
    [MatchType.ALL]: RulesetBooleanOperatorEnum.AND,
    [MatchType.ANY]: RulesetBooleanOperatorEnum.OR,
  };

  readonly translations = {
    all: $localize`:@@shared.all:All`,
    any: $localize`:@@shared.any:Any`,
  };

  ngOnInit(): void {
    this.setSelectedMatchTypeIndex(this.formGroup().get("booleanOperator").value);

    this.subscriptions.add(
      this.formGroup()
        .get("booleanOperator")
        .valueChanges.subscribe((value) => {
          this.setSelectedMatchTypeIndex(value);
        }),
    );

    this.subscriptions.add(
      this.formGroup()
        .get("groupForms")
        .valueChanges.subscribe((groupFormValues) => {
          if (groupFormValues.length === 1) {
            this.formGroup().get("booleanOperator").setValue(RulesetBooleanOperatorEnum.AND);
          }
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setSelectedMatchTypeIndex(value: string) {
    this.selectedMatchTypeIndex.set(
      value === RulesetBooleanOperatorEnum.AND ? MatchType.ALL : MatchType.ANY,
    );
  }

  get conditionItemFormControls() {
    const groupForms = this.formGroup().get("groupForms") as FormArray<
      FormGroup<IConditionGroupForm>
    >;

    return groupForms.controls;
  }

  onConvertToGroup(criteriaItemIndex: number, criteriaGroupIndex: number) {
    const groupForms = this.formGroup().get("groupForms") as FormArray<
      FormGroup<IConditionGroupForm>
    >;

    const itemForms = groupForms.at(criteriaGroupIndex).get("itemForms") as FormArray;

    const conditionTypeValue = itemForms.at(criteriaItemIndex) as FormGroup<IConditionItemForm>;

    const newConditionItemForm = this.formBuilder.group({
      booleanOperator: [RulesetBooleanOperatorEnum.AND, CustomValidators.required],
      itemForms: this.formBuilder.array([
        this.rulesetsBuilderService.buildConditionItemForm(conditionTypeValue),
      ]),
    }) as FormGroup<IConditionGroupForm>;

    itemForms.removeAt(criteriaItemIndex);
    groupForms.insert(criteriaGroupIndex, newConditionItemForm);
  }

  onMatchTypeChange(index: MatchType) {
    const booleanOperator =
      index === MatchType.ALL ? RulesetBooleanOperatorEnum.AND : RulesetBooleanOperatorEnum.OR;

    this.selectedMatchTypeIndex.set(index);

    this.formGroup().patchValue({ booleanOperator });
  }

  onAddCriteriaGroup() {
    const groupForms = this.formGroup().get("groupForms") as FormArray<
      FormGroup<IConditionGroupForm>
    >;

    const newConditionItemForm = this.formBuilder.group({
      booleanOperator: [RulesetBooleanOperatorEnum.AND, CustomValidators.required],
      itemForms: this.formBuilder.array([
        this.rulesetsBuilderService.buildDefaultConditionItemForm(),
      ]),
    }) as FormGroup<IConditionGroupForm>;

    groupForms.push(newConditionItemForm);
  }

  onRemoveCriteriaGroup(index: number) {
    const groupForms = this.formGroup().get("groupForms") as FormArray<
      FormGroup<IConditionGroupForm>
    >;

    groupForms.removeAt(index);
  }
}
